/** @format */
import React, { useState, useEffect, useMemo } from 'react'
import { _$, Icon, Button, Dialog, Message, Tooltip } from '@king-design/react'
import { useParams, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import _ from 'lodash'
import { Divider, TablePage, MonitorSearch } from 'src/components'
import { ITargetTabsProps } from './TargetTabs'
import NoDataComponent from 'src/components/NoData'
import API from 'src/api/ApiMonitorCustomAlarm'
import { request, getParentUrl, getRamNumber } from 'src/utils'
import variable from 'src/static/styles/var'

interface TabAllTargeProps extends ITargetTabsProps {
  updateLocalMetricList: (value: any) => void
}

const TabAllTarget: React.FC<TabAllTargeProps> = ({
  className,
  region,
  nameSpace,
  metricTotalCount,
  queryNameSpace,
  setQueryNameSpace,
  queryMetric,
  setQueryMetric,
  updateLocalMetricList,
  getNameSpace,
  getAllMetricList
}) => {
  // params
  const { metricKey: urlMetricKey, namespace: urlNamespace, dimension } = useParams<any>()
  // react-router history
  const history = useHistory()
  // 所有指标数量
  const [metricCurrentCount, setMetricCurrentCount] = useState<number>()
  // nameSpace=命名空间list页、metricList=指标list页
  const [currentPage, setCurrentPage] = useState<'nameSpace' | 'metricList'>('nameSpace')
  // 当前选中命名空间
  const [currentNameSpace, setCurrentNameSpace] = useState<string>('')
  // 指标list
  const [metricList, setMetricList] = useState<any>([])
  // 指标list 已勾选的项（uniqueId）
  const [checkedMetric, setCheckedMetric] = useState<any[]>([])
  // 删除指标失败后提示框
  const [dialogVisible, setDialogVisible] = useState<boolean>(false)
  // 删除指标失败数据
  const [dialogData, setDialogData] = useState<any[]>([])

  // 计算所有指标数量
  useEffect(() => {
    if (nameSpace?.length) {
      const count = nameSpace.reduce((pre, cur) => pre + cur.metricCount, 0)
      setMetricCurrentCount(count || 0)
    } else {
      setMetricCurrentCount(0)
    }
  }, [nameSpace])

  // 切换region时候 返回命名空间页
  useEffect(() => {
    if (region) {
      setCurrentPage('nameSpace')
      setCurrentNameSpace('')
    }
  }, [region])

  useEffect(() => {
    handleUrlParams()
  }, [urlNamespace, urlMetricKey, dimension])

  // 通过url设置指标
  const handleUrlParams = async () => {
    if (urlNamespace && urlMetricKey) {
      localStorage.setItem('metricList', '[]')
      const result = await getMetricList(urlNamespace)
      const find = result.find(
        item => item.metricName === urlMetricKey && item.dimension === dimension
      )
      batchAddChart(find)
    }
  }

  // 获取指标list
  const getMetricList = async (value = currentNameSpace, event = null) => {
    const eventTarget = event?.target
    if (!eventTarget || !eventTarget.classList.contains('k-icon')) {
      if (value) {
        if (currentPage === 'nameSpace') {
          setCurrentPage('metricList')
        }
        setCurrentNameSpace(value)
        const result = await request(API.getDescribeMetrics, {
          data: {
            region,
            page: 1,
            pageSize: 500,
            nameSpace: value,
            query: queryMetric
          }
        })
        if (urlNamespace && urlMetricKey) {
          const find = result?.data?.metricList.find(
            item => item.metricName === urlMetricKey && item.dimension === dimension
          )
          find && updateTableChartStatus(result?.data?.metricList || [], [find])
        } else {
          updateTableChartStatus(result?.data?.metricList || [])
        }
        return result?.data?.metricList || []
      }
    }
  }

  useEffect(() => {
    getMetricList()
  }, [queryMetric])

  // 设置metricList中数据的绘图状态
  const updateTableChartStatus = (
    data = metricList,
    localList = JSON.parse(localStorage.getItem('metricList')) || []
  ) => {
    setQueryNameSpace('') // 清空搜索框
    if (data.length) {
      const metricListData = _.cloneDeep(data)
      const updateMetricList = metricListData.map(item => {
        const find = localList.find(localItem => item.uniqueId === localItem.uniqueId)
        return find ? { ...item, isChart: true } : { ...item, isChart: false }
      })
      setMetricList(updateMetricList)
    } else {
      setMetricList([])
    }
  }

  // 删除命名空间
  const deleteNameSpace = async item => {
    if (item.metricCount === 0) {
      Dialog.confirm({
        content: _$(`您确认删除命名空间？删除后不可恢复！`, { nameSpace })
      }).then(() => {
        _removeNamespaces(item)
      })
    } else {
      Message.error(_$(`命名空间下含有指标，无法删除`, { nameSpace }), 3000)
    }
  }

  // 删除命名空间async
  const _removeNamespaces = async item => {
    if (item) {
      const result = await request(`${API.removeNamespaces}?region=${region}`, {
        data: {
          ...item
        },
        method: 'POST'
      })
      if (result?.code === 'Success') {
        Message.success(result?.message)
        getNameSpace()
      }
    }
  }

  // 批量新建绘图
  const batchAddChart = (item?) => {
    // // 已经选中的指标(批量、单个)
    const checkedArray = item
      ? [item]
      : metricList.filter(item => checkedMetric.includes(item.uniqueId))
    // 设置local内已绘图的指标
    const localMetricList = JSON.parse(localStorage.getItem('metricList')) || []
    // 去重复、并且添加初始值
    const mergeMtricList = [...localMetricList, ...checkedArray]
      .reduce((pre, cur) => {
        const find = pre.find(preItem => preItem.uniqueId === cur.uniqueId)
        return find ? pre : [...pre, cur]
      }, [])
      .map(item => ({
        metricLegend: item.metricName,
        alias: item.metricName,
        method: 'avg',
        period: 1,
        ...item,
        isChart: true
      }))
    // 遍历判断重名的话后面加6位随机数
    const renameList = []
    mergeMtricList.forEach(cur => {
      const { uniqueId, alias } = cur
      const find = renameList.some(item => {
        return item.uniqueId !== uniqueId && item.alias === alias
      })
      if (find) {
        cur.alias = `${cur.alias}-${getRamNumber(6)}`
      }
      renameList.push(cur)
    })
    // 如果数量不相等就重新请求一次所有指标list
    if (metricTotalCount !== metricCurrentCount) {
      getAllMetricList()
    }
    updateLocalMetricList(renameList)
    metricList?.length && updateTableChartStatus(undefined, renameList)
    setCheckedMetric([])
  }

  // 批量删除绘图 item=uniqueId[]
  const batchDeleteChart = (item?) => {
    let checkedArray
    if (item) {
      checkedArray = Array.isArray(item) ? item : [item]
    } else {
      checkedArray = checkedMetric
    }
    const localMetricList = JSON.parse(localStorage.getItem('metricList')) || []
    const updateLocal = localMetricList.filter(
      localItem => !checkedArray.includes(localItem.uniqueId)
    )
    updateLocalMetricList(updateLocal)
    updateTableChartStatus()
    setCheckedMetric([])
  }

  // 批量删除指标
  const batchDeleteTarget = (item?) => {
    const checkedArray = item
      ? [item]
      : metricList.filter(item => checkedMetric.includes(item.uniqueId))
    // 删除指标提示
    setDialogVisible(true)
    setDialogData(checkedArray)
  }

  // 删除指标async
  const _removeMetric = async metricList => {
    const { data } = await request(`${API.removeMetrics}?region=${region}`, {
      data: {
        totalCount: metricList.length,
        metricList
      },
      method: 'POST'
    })
    // 删除成功的数据从绘图中也删掉
    const deleteUniqueId = metricList
      .filter(item => {
        const findItem = [].concat(data).find(dataItem => dataItem.uniqueId === item.uniqueId)
        return !findItem
      })
      .map(({ uniqueId }) => uniqueId)
    batchDeleteChart(deleteUniqueId)
    // 重新获取指标list
    getMetricList()
    if (!data || !data.length) {
      Message.success(_$('删除成功'), 3000)
    } else {
      if (data.length !== metricList.length) {
        Message.success(_$('部分指标删除成功'), 3000)
      }
      // 没有删除成功数据的metricName集合
      const updateDialogData = data.map(({ metricName }) => metricName)
      Dialog.confirm({
        title: _$('指标删除失败'),
        content: _$('指标已新建警报的指标，请您删除相应的警报', {
          msg: updateDialogData.join('、')
        }),
        okText: _$('删除警报')
      }).then(
        () => {
          window.parent.location.href = `${getParentUrl()}#/monitor/custom/alarm`
        },
        () => {
          setDialogVisible(false)
          setDialogData([])
        }
      )
    }
  }

  // table scheme
  const scheme = useMemo(() => {
    return {
      metricName: {
        title: (
          <>
            <span>{_$('指标名称')}</span>
            <Tooltip content={_$('最大展示500个指标')}>
              <Icon className="theadicon ion-information-circled"></Icon>
            </Tooltip>
          </>
        )
      },
      dimension: {
        title: (
          <>
            <span>{_$('维度')}</span>
          </>
        )
      },
      isChart: {
        title: _$('是否新建至绘图'),
        template: item => (item.isChart ? _$('已新建') : _$('未新建'))
      },
      active: {
        title: _$('操作'),
        template: function (item) {
          return (
            <div className="ksc-space">
              {item.isChart ? (
                <a className="primary-color" onClick={() => batchDeleteChart(item.uniqueId)}>
                  {_$('删除绘图')}
                </a>
              ) : (
                <a className="primary-color" onClick={() => batchAddChart(item)}>
                  {_$('新建绘图')}
                </a>
              )}
              <Divider />
              <a className="primary-color" onClick={() => batchDeleteTarget(item)}>
                {_$('删除指标')}
              </a>
            </div>
          )
        }
      }
    }
  }, [metricList])

  return (
    <div className={className}>
      {/* 命名空间page */}
      {currentPage === 'nameSpace' && (
        <>
          <div className="header">
            <p className="target-num">
              {_$('指标数')}
              <span className="primary-color">{metricCurrentCount}</span>
            </p>
            <div className="filter">
              <MonitorSearch
                placeholder={_$('输入指标名称、维度搜索123')}
                value={queryNameSpace}
                onChange={value => {
                  setQueryNameSpace(value)
                }}
              />
              {/* <Input
                placeholder={_$('输入指标名称、维度搜索')}
                clearable={true}
                value={queryNameSpace}
                onChangeValue={value => {
                  setQueryNameSpace(value)
                }}
                onKeyUp={e => {
                  if (e.keyCode === 13) {
                    getNameSpace()
                  }
                }}
                onClear={() => {
                  setQueryNameSpace(() => '')
                  getNameSpace()
                }}
                slotSuffix={
                  <React.Fragment>
                    <Icon
                      className="k-icon-search cursor-pointer"
                      onClick={() => {
                        getNameSpace()
                      }}
                    />
                  </React.Fragment>
                }
              ></Input> */}
            </div>
          </div>
          <div className="target-list">
            {nameSpace.length ? (
              nameSpace.map(item => (
                <div
                  key={item.nameSpace}
                  title={item.nameSpace}
                  className="target-item cursor-pointer"
                  onClick={e => getMetricList(item.nameSpace, e)}
                >
                  <Icon
                    className="ion-trash-a"
                    color={variable.primaryColor}
                    onClick={() => {
                      deleteNameSpace(item)
                    }}
                  ></Icon>
                  <p className="title primary-color">{item.nameSpace}</p>
                  <p className="num">
                    {item.metricCount}
                    {_$('个指标')}
                  </p>
                </div>
              ))
            ) : (
              <NoDataComponent />
            )}
          </div>
        </>
      )}

      {/* 指标page */}
      {currentPage === 'metricList' && (
        <>
          <div className="table-header">
            <div
              className="back-wrap cursor-pointer text-overflow"
              title={currentNameSpace}
              onClick={() => {
                setCurrentPage('nameSpace')
                setMetricList([])
                getNameSpace()
                if (urlMetricKey && urlNamespace) {
                  history.push('/monitor/custom/metric')
                }
              }}
            >
              <Icon className="ion-chevron-left" />
              <span>{currentNameSpace}</span>
            </div>
          </div>
          <div className="table-filter">
            <div className="table-filter-button">
              <Button
                type="primary"
                disabled={!checkedMetric.length}
                onClick={() => batchAddChart()}
              >
                {_$('批量新建绘图')}
              </Button>
              <Button
                disabled={!checkedMetric.length}
                onClick={() => {
                  batchDeleteChart()
                }}
              >
                {_$('批量删除绘图')}
              </Button>
              <Button
                disabled={!checkedMetric.length}
                onClick={() => {
                  batchDeleteTarget()
                }}
              >
                {_$('批量删除指标')}
              </Button>
            </div>
            <div className="filter">
              <MonitorSearch
                placeholder={_$('输入指标名称、维度搜索')}
                value={queryMetric}
                onChange={value => {
                  setQueryMetric(value)
                }}
              />
            </div>
          </div>
          {scheme ? (
            <TablePage
              tableProps={{
                scheme: scheme,
                data: metricList,
                checkedKeys: checkedMetric,
                rowCheckable: false,
                resizable: true,
                rowKey: item => item.uniqueId,
                onChangeCheckedKeys: value => {
                  setCheckedMetric(value)
                }
              }}
              showPage={false}
            ></TablePage>
          ) : null}
        </>
      )}

      {/* dialog提示框 */}
      <Dialog
        width={600}
        value={dialogVisible}
        onClose={() => {
          setDialogVisible(false)
          setDialogData([])
        }}
        okText={_$('确定')}
        onOk={() => {
          // setDialogVisible(false)
          _removeMetric(dialogData)
        }}
        title={_$('删除指标')}
      >
        <div className="dialog-body">
          <p style={{ margin: 0, fontSize: '14px', textAlign: 'center' }}>
            {dialogData?.length > 1
              ? _$('您确认删除当前所选指标？删除后指标所有数据将一并删除，不可恢复！')
              : `您确认删除【${dialogData[0]?.metricName}】指标？删除后指标所有数据将一并删除，不可恢复！`}
          </p>
        </div>
      </Dialog>
    </div>
  )
}

export default styled(TabAllTarget)`
  padding: 16px 0;
  p {
    margin-bottom: 0;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .table-header {
      display: flex;
      align-items: center;
      p {
        font-size: 14px;
      }
      & > * {
        margin-right: 16px;
      }
    }
    .target-num {
      font-size: 14px;
      span {
        margin-left: 5px;
      }
    }
    .filter {
      display: flex;
      align-items: center;
      .k-input {
        margin-right: 16px;
      }
    }
  }
  .table-header {
    display: flex;
    .back-wrap {
      max-width: 50%;
      .k-icon {
        margin-right: 10px;
      }
      span {
        font-size: 16px;
      }
      &:hover {
        color: ${variable.primaryColor};
      }
    }
  }
  .table-filter {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    .table-filter-button {
      display: flex;
      & > * {
        margin-right: 16px;
      }
    }
  }
  .target-list {
    display: flex;
    flex-wrap: wrap;
    .target-item {
      width: calc(12.5% - 16px);
      min-width: 240px;
      height: 100px;
      margin: 8px;
      border: 1px solid #d7d7d7;
      background: #fff;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.04);
      transition: all ease-in-out 0.2s;
      position: relative;
      .k-icon {
        position: absolute;
        top: 5px;
        right: 10px;
        opacity: 0;
        transition: all ease-in-out 0.2s;
      }
      .title {
        width: 100%;
        text-align: center;
        font-size: 18px;
        padding: 0 5px;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        word-break: break-all;
      }
      &:hover {
        box-shadow: 0 3px 10px 2px rgba(0, 0, 0, 0.16);
        .k-icon {
          opacity: 1;
        }
      }
    }
  }
  .k-table {
    margin-top: 16px;
  }
  .theadicon {
    cursor: pointer;
    vertical-align: middle;
    font-size: 14px;
    margin-left: 4px;
  }
`
